import {logger} from 'common'
import {FormikErrors, FormikTouched} from 'formik'
import _ from 'lodash'

import {ButtonStatus} from '@/components/common/content/Button'
import {
  BUTTON_ERROR_MIN_DURATION,
  BUTTON_SUBMITTING_MIN_DURATION,
  BUTTON_SUCCESS_MIN_DURATION,
} from '@/theme/cssTransitions'
import {vpnAccessDisabledHttpStatusCode} from '@/utils/constants'

export const getTouchedFromErrors = <T>(errors: FormikErrors<T>): FormikTouched<T> => {
  if (_.isPlainObject(errors)) {
    return _.mapValues(errors, (value) => getTouchedFromErrors(value as any)) as FormikTouched<T>
  }

  return true as any
}

// Prevents the submitting state from flashing off too quickly when a request resolves faster than the threshold.
export const enforceFormSubmittingMinimumDuration = async (
  startTime: number,
  delay: number = BUTTON_SUBMITTING_MIN_DURATION,
) => {
  const elapsed = Date.now() - startTime
  if (elapsed < delay) {
    await new Promise((resolve) => setTimeout(resolve, delay - elapsed))
  }
}

// Always holds the result (success/error) state for the full duration, regardless of when the request completed.
export const enforceFormResultMinimumDuration = async (delay: number) => {
  await new Promise((resolve) => setTimeout(resolve, delay))
}

/**
 * Handles form submission with button state transitions (idle → submitting → success/error → idle),
 * enforcing minimum display durations so state changes are always visible to the user.
 *
 * @param setStatus - Callback that updates the button's visual state.
 * @param onSubmit - Async function that performs the API call and returns a `Response`.
 * @param onSuccess - Optional callback called with the parsed response data on success; runs outside
 *   the try/catch so navigation errors cannot accidentally trigger the error state.
 * @param showSuccessStatus - When `false`, skips the success state and transitions directly to idle
 *   on success. Defaults to `true`.
 * @param keepSuccessStatus - When `true`, does not reset the button back to idle after `onSuccess`.
 *   Use when the form stays mounted under a success modal (e.g. "report a threat") so the button
 *   doesn't flash back to idle while the modal is visible. Defaults to `showSuccessStatus` (there's
 *   no success state to keep once `showSuccessStatus` is `false`).
 * @param onError - Optional callback receiving a translation key: `'error-vpn-access-disabled'`
 *   or `'api-error-description'`.
 * @param errorPath - Label prepended to error log messages to identify the call site.
 * @param checkVpnError - When `true`, checks whether a failed request was blocked by VPN and passes
 *   `'error-vpn-access-disabled'` to `onError`. Only needed for VPN-guarded endpoints
 *   (`/api/request`, `/api/service-request`).
 */
export const submitWithButtonStatus = async <T>({
  setStatus,
  onSubmit,
  onSuccess,
  showSuccessStatus,
  keepSuccessStatus = showSuccessStatus !== false,
  onError,
  errorPath,
  checkVpnError,
}: {
  setStatus: (status: ButtonStatus) => void
  onSubmit: () => Promise<Response>
  onSuccess?: (data: T) => Promise<void> | void
  showSuccessStatus?: boolean
  keepSuccessStatus?: boolean
  onError?: (errorKey: string, message: string) => void
  errorPath: string
  checkVpnError?: boolean
}): Promise<void> => {
  const startTime = Date.now()
  setStatus(ButtonStatus.submitting)

  const handleError = async (logContext: object, message: string, errorKey: string) => {
    setStatus(ButtonStatus.error)
    onError?.(errorKey, message)
    logger.error(logContext, `${errorPath} - ${message}`)
    await enforceFormResultMinimumDuration(BUTTON_ERROR_MIN_DURATION)
    setStatus(ButtonStatus.idle)
    return {ok: false} as const
  }

  const result = await (async (): Promise<{ok: true; data: T} | {ok: false}> => {
    try {
      const response = await onSubmit()
      await enforceFormSubmittingMinimumDuration(startTime)
      const isJson = response.headers.get('content-type')?.includes('application/json')
      const result = isJson ? await response.json() : {}

      if (!response.ok) {
        const vpnError = checkVpnError && response.status === vpnAccessDisabledHttpStatusCode
        return handleError(
          {result},
          result.message ?? 'Error occurred',
          vpnError ? 'error-vpn-access-disabled' : 'api-error-description',
        )
      }

      if (showSuccessStatus !== false) {
        setStatus(ButtonStatus.success)
        await enforceFormResultMinimumDuration(BUTTON_SUCCESS_MIN_DURATION)
      }

      return {ok: true, data: result}
    } catch (e) {
      await enforceFormSubmittingMinimumDuration(startTime)
      return handleError(
        e as object,
        e instanceof Error ? e.message : 'Error occurred',
        e instanceof Error ? e.message : 'api-error-description',
      )
    }
  })()

  // onSuccess runs outside the try/catch so a redirect/navigation error can't accidentally trigger the error state
  if (result.ok) {
    await onSuccess?.(result.data)
    if (!keepSuccessStatus) {
      setStatus(ButtonStatus.idle)
    }
  }
}
